import {
  useCSMs,
  type TCompanyHandler,
} from '@commenda/api';
import { SingleCombobox } from '@commenda/kakashi';
import { CompanyHandlers } from '@commenda/utils';
import { CompanyHandlerType } from '@prisma/client';
import { useMemo } from 'react';

const UNASSIGNED = '__unassigned__';

interface AccountManagerComboboxProps {
  /** The currently selected handler id. `null` means "unassigned". */
  value: string | null;
  /** Called with `null` when the user picks "Unassigned" (or clears). */
  onChange: (value: string | null) => void;
  /**
   * If the current handler isn't in the eligible list (e.g. a legacy handler
   * without `isCommendaAdmin` or without a `@commenda.io` email), pass it here
   * so the control keeps rendering the correct selected label instead of
   * silently swapping to an unrelated handler.
   */
  currentHandler?: Pick<TCompanyHandler, 'id' | 'name' | 'email'> | null;
  /** Render an "Unassigned" option. Defaults to true. */
  allowUnassigned?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  className?: string;
}

/**
 * Account-manager picker used across admin UIs.
 *
 * Sources eligible CSMs from `/company-handlers/eligible`, which returns
 * users where `isCustomerSuccessManager = true`.
 */
export function AccountManagerCombobox({
  value,
  onChange,
  currentHandler,
  allowUnassigned = true,
  placeholder = 'Select account manager',
  searchPlaceholder = 'Search account managers…',
  disabled = false,
  error,
  id,
  className,
}: AccountManagerComboboxProps) {
  const { data } = useCSMs();

  const options = useMemo(() => {
    const eligible = CompanyHandlers.filterEligible(data?.handlers ?? []);
    const withFallback = CompanyHandlers.withFallbackForSelect(
      eligible,
      currentHandler ?? null,
      (c) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        type: CompanyHandlerType.ACCOUNT_MANAGER,
        role: '',
        createdAt: '',
        lastUpdatedAt: '',
      })
    );

    const handlerOptions = withFallback.map((h) => ({
      value: h.id,
      label: h.name?.trim() || h.email,
    }));

    return allowUnassigned
      ? [{ value: UNASSIGNED, label: 'Unassigned' }, ...handlerOptions]
      : handlerOptions;
  }, [data?.handlers, currentHandler, allowUnassigned]);

  return (
    <SingleCombobox
      _className={className}
      id={id}
      value={value ?? (allowUnassigned ? UNASSIGNED : '')}
      setValue={(next) => {
        if (!next || next === UNASSIGNED) {
          onChange(null);
          return;
        }
        onChange(next);
      }}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      notFoundText="No account managers found."
      disabled={disabled}
      error={error}
      canClearSelection={allowUnassigned}
    />
  );
}
